import { defaultOptinValues } from '@systemeio/digital-store-shared'
import { LanguageCodeEnum, toast } from '@systemeio/ui'
import ItemActions from 'modules/stores/store/components/item-actions'
import ItemTabs, { ItemTabType } from 'modules/stores/store/components/item-tabs'
import ThankYouTab from 'modules/stores/store/components/tabs/thank-you-tab'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import OptinThumbnail from 'modules/stores/store/content/optin/components/optin-thumbnail'
import { defaultErrors } from 'modules/stores/store/content/optin/constants/default-optin-errors'
import { optinInitFields } from 'modules/stores/store/content/optin/constants/mandatory-fields'
import {
  OptinTabEnum,
  optinTabLocoKeys,
} from 'modules/stores/store/content/optin/enum/optin-tab-enum'
import { useOptinActions } from 'modules/stores/store/content/optin/hooks/use-optin-actions'
import {
  OptinItemEditType,
  OptinItemErrorsType,
  OptinItemViewType,
} from 'modules/stores/store/content/optin/types/optin-interface'
import { getOptinUpdateData } from 'modules/stores/store/content/optin/utils/get-optin-update-data'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreContactFields } from 'modules/stores/store/hooks/use-store-contact-fields'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreId } from 'modules/stores/types/stores-interface'
import { FC, useEffect, useState } from 'react'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { deepCompare } from 'shared/utils/deepCompare'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { twMerge } from 'tailwind-merge'
import { useStoreEditItemContext } from '../../context/store-item-edit-context'
import OptinOptions from './components/options'
import { useErrorMessageInTabs } from './hooks/use-error-message-in-tabs'

interface OptinEditProps {
  storeId: StoreId
  optin?: OptinItemViewType
  languageCode: LanguageCodeEnum
}

const OptinEdit: FC<OptinEditProps> = ({ storeId, optin, languageCode }) => {
  const { t } = useLocoTranslation()

  const [tab, setTab] = useState<OptinTabEnum>(OptinTabEnum.thumbnail)

  const { redirectToMainPage } = useStoreQuery()

  const { mutate } = useStore(storeId)
  const { getTabsWithErrors, formErrorMessage } = useErrorMessageInTabs()

  const defaultValues: OptinItemEditType = {
    ...defaultOptinValues,
    draft: false,
    title: t('dashboard.store.optin.title_default', { lng: languageCode }),
    subTitle: t('dashboard.store.optin.subtitle_default', { lng: languageCode }),
    buttonTitle: t('dashboard.store.optin.button_default', {
      lng: languageCode,
    }),
    thankYouPage: {
      title: t('dashboard.store.optin.thank_you_title_default', {
        lng: languageCode,
      }),
      text: t('dashboard.store.optin.thank_you_text_default', {
        lng: languageCode,
      }),
    },
  }

  const [tempState, setTempState] = useState<OptinItemEditType>(optin || defaultValues)

  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)

  const { errors: initialErrors, setErrors: setEditItemErrors } = useStoreEditItemContext()
  const [errors, setErrors] = useState<OptinItemErrorsType>({
    ...defaultErrors,
    ...initialErrors,
  })

  useEffect(() => {
    return () => {
      setEditItemErrors({})
    }
  }, [])

  const [isFetchingPublish, setIsFetchingPublish] = useState(false)
  const [isFetchingDraft, setIsFetchingDraft] = useState(false)

  useStoreContactFields({
    savedFields: tempState.fields,
    initFields: optinInitFields,
    onChange: fields => setTempState(prev => ({ ...prev, fields })),
  })

  const { createOptin, updateOptin } = useOptinActions()

  const onSave = async (optin: OptinItemEditType) => {
    try {
      setErrors(defaultErrors)

      if (optin.id) {
        const item = await updateOptin(optin.id, getOptinUpdateData(optin))
        await mutate(
          data =>
            data && {
              ...data,
              items: data.items.map(i => (i.itemId === item.itemId ? item : i)),
            },
          false,
        )
      } else {
        const item = await createOptin(storeId, getOptinUpdateData(optin))

        await mutate(data => data && { ...data, items: [...data.items, item] }, false)
      }

      toast.success(t('global.changes_saved'))

      await redirectToMainPage()
    } catch (e) {
      const errors = handle422Error(e, setErrors) as OptinItemErrorsType | undefined
      if (errors) {
        const { errorMessage } = formErrorMessage({ errors })
        toast.error(errorMessage)
      }
    } finally {
      setIsFetchingPublish(false)
      setIsFetchingDraft(false)
    }
  }

  const onPublish = async () => {
    const optin: OptinItemEditType = { ...tempState, draft: false }
    setIsFetchingPublish(true)
    setTempState(optin)
    await onSave(optin)
  }

  const onSaveDraft = async () => {
    const optin: OptinItemEditType = { ...tempState, draft: true }
    setIsFetchingDraft(true)
    setTempState(optin)
    await onSave(optin)
  }

  const isLastTab = tab === OptinTabEnum.thankYou || tab === OptinTabEnum.options

  const isReady =
    !!tempState.title &&
    !!tempState.subTitle &&
    !!tempState.buttonTitle &&
    !!tempState.fields.length &&
    !!tempState.thankYouPage.title &&
    !!tempState.thankYouPage.text

  const isCreatingProcess = !optin

  const errorsByTab = getTabsWithErrors({ errors })

  const tabs: ItemTabType<OptinTabEnum>[] = [
    {
      key: OptinTabEnum.thumbnail,
      caption: t(optinTabLocoKeys[OptinTabEnum.thumbnail]),
      hasError: errorsByTab[OptinTabEnum.thumbnail],
    },
    {
      key: OptinTabEnum.thankYou,
      caption: t(optinTabLocoKeys[OptinTabEnum.thankYou]),
      hasError: errorsByTab[OptinTabEnum.thankYou],
    },
    {
      key: OptinTabEnum.options,
      caption: t(optinTabLocoKeys[OptinTabEnum.options]),
      hasError: errorsByTab[OptinTabEnum.options],
    },
  ]

  useEffect(() => {
    if (optin) {
      setIsDataSaved(deepCompare(tempState, optin))
    } else {
      setIsDataSaved(deepCompare(tempState, { ...defaultValues, fields: tempState.fields }))
    }
  }, [tempState])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemTabs tabs={tabs} currentTab={tab} setTab={setTab} />
      {tab === OptinTabEnum.thumbnail && (
        <OptinThumbnail tempState={tempState} setTempState={setTempState} errors={errors} />
      )}
      {tab === OptinTabEnum.thankYou && (
        <ThankYouTab
          thankYouPage={tempState.thankYouPage}
          onChange={thankYouPage => setTempState(prev => ({ ...prev, thankYouPage }))}
          errors={errors}
        />
      )}
      {tab === OptinTabEnum.options && <OptinOptions storeId={storeId} optinId={optin?.id} />}
      <ItemActions
        publish={{
          disabled: !isReady,
          onClick: isLastTab || !isCreatingProcess ? onPublish : undefined,
          isFetching: isFetchingPublish,
        }}
        saveDraft={{ onClick: onSaveDraft, isFetching: isFetchingDraft }}
        next={{
          onClick:
            isCreatingProcess && !isLastTab ? () => setTab(OptinTabEnum.thankYou) : undefined,
        }}
      />
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.store.confirmation_message_before_exit')}
        hideModal={isDataSaved || isFetchingPublish || isFetchingDraft}
      />
    </div>
  )
}

export default OptinEdit
